import React, { useMemo, useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { apiService } from "../../../services/api";
import DateInputDDMMYYYY from "../../../components/DateInputDDMMYYYY";

interface LinkAccessData {
  date: string;
  accessCount: number;
}

interface Video3DViewsData {
  date: string;
  views: number;
}

interface CombinedAccessViewsChartProps {
  linkAccessData?: LinkAccessData[];
  video3DViewsData?: Video3DViewsData[];
}

const CombinedAccessViewsChart: React.FC<CombinedAccessViewsChartProps> = ({
  linkAccessData = [],
  video3DViewsData = [],
}) => {
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const formatDateYYYYMMDD = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const subtractOneMonth = (date: Date) => {
    const d = new Date(date);
    const dayOfMonth = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    const lastDayOfTargetMonth = new Date(
      d.getFullYear(),
      d.getMonth() + 1,
      0
    ).getDate();
    d.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
    return d;
  };

  const defaultEndDate = useMemo(() => formatDateYYYYMMDD(new Date()), []);
  const defaultStartDate = useMemo(
    () => formatDateYYYYMMDD(subtractOneMonth(new Date())),
    []
  );

  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  const [selectedType, setSelectedType] = useState<"link" | "button" | "explore" | "all">("all");
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [apiLinkData, setApiLinkData] = useState<LinkAccessData[]>([]);
  const [apiVideoData, setApiVideoData] = useState<Video3DViewsData[]>([]);
  const [apiExploreData, setApiExploreData] = useState<LinkAccessData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // Fetch access data from API
  useEffect(() => {
    const fetchAccessData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Build API parameters from filters
        const params: {
          month?: number;
          startDate?: string;
          endDate?: string;
          type?: "link" | "button" | "explore";
        } = {};

        const effectiveStartDate = startDate || defaultStartDate;
        const effectiveEndDate = endDate || defaultEndDate;
        
        if (selectedMonth !== null) {
          params.month = selectedMonth;
        }
        params.startDate = effectiveStartDate;
        params.endDate = effectiveEndDate;
        if (selectedType !== "all") {
          params.type = selectedType;
        }
        
        // Fetch main data
        const response = await apiService.getAccessData(params);
        
        if (selectedType === "explore") {
          // When filtering by explore, use exploreAccess from response
          setApiLinkData(response.exploreAccess || []);
          setApiVideoData([]);
          setApiExploreData([]);
        } else {
          setApiLinkData(response.linkAccess || []);
          setApiVideoData(response.video3DViews || []);
          
          // Fetch explore data separately when showing all types (to display explore line in chart)
          if (selectedType === "all") {
            const exploreParams = {
              ...params,
              type: "explore" as const,
            };
            const exploreResponse = await apiService.getAccessData(exploreParams);
            // Use exploreAccess from response if available, otherwise fallback to linkAccess
            setApiExploreData(exploreResponse.exploreAccess || exploreResponse.linkAccess || []);
          } else {
            setApiExploreData([]);
          }
        }
      } catch (err: any) {
        console.error("Error fetching access data:", err);
        setError(err.message || "Không thể tải dữ liệu truy cập");
        // Set empty arrays on error
        setApiLinkData([]);
        setApiVideoData([]);
        setApiExploreData([]);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if no data provided via props
    if (linkAccessData.length === 0 && video3DViewsData.length === 0) {
      fetchAccessData();
    } else {
      setLoading(false);
    }
  }, [
    linkAccessData.length,
    video3DViewsData.length,
    selectedMonth,
    startDate,
    endDate,
    selectedType,
    defaultStartDate,
    defaultEndDate,
  ]);

  // Use props data if provided, otherwise use API data
  const displayLinkData = linkAccessData.length > 0 
    ? linkAccessData 
    : apiLinkData;
  
  const displayVideoData = video3DViewsData.length > 0 
    ? video3DViewsData 
    : apiVideoData;

  // Combine data (filtering is now done on server-side via API)
  const filteredData = useMemo(() => {
    // Get all unique dates from all datasets
    const allDates = new Set<string>();
    displayLinkData.forEach((item) => allDates.add(item.date));
    displayVideoData.forEach((item) => allDates.add(item.date));
    apiExploreData.forEach((item) => allDates.add(item.date));

    // Create combined data structure
    const combined = Array.from(allDates).map((date) => {
      const linkItem = displayLinkData.find((item) => item.date === date);
      const videoItem = displayVideoData.find((item) => item.date === date);
      const exploreItem = apiExploreData.find((item) => item.date === date);

      let linkAccess = 0;
      let exploreAccess = 0;
      
      if (selectedType === "explore") {
        // When filtering by explore, linkItem contains explore data
        exploreAccess = linkItem?.accessCount || 0;
      } else {
        // When showing all or other types
        linkAccess = linkItem?.accessCount || 0;
        // Explore access comes from apiExploreData (only populated when selectedType === "all")
        exploreAccess = exploreItem?.accessCount || 0;
      }

      const videoViews = videoItem?.views || 0;
      // Số người chỉ vào link không xem video = tổng vào link - số người xem video
      const linkOnlyAccess = Math.max(0, linkAccess - videoViews);

      return {
        date,
        linkAccess,
        videoViews,
        linkOnlyAccess,
        exploreAccess,
      };
    });

    // Sort by date
    return combined.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [displayLinkData, displayVideoData, apiExploreData, selectedType]);

  // Prepare chart data
  const chartData = useMemo(() => {
    // Get all unique dates for labels
    const allDates = Array.from(new Set(filteredData.map(item => item.date)))
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    const labels = allDates.map((date) => {
      const d = new Date(date);
      return d.toLocaleDateString("vi-VN", {
        month: "short",
        day: "numeric",
      });
    });

    // Create datasets - always show all 4 datasets, set 0 for non-matching types
    const datasets: any[] = [];

    // Dataset 1: Tổng số người vào link
    const totalLinkAccessData = allDates.map((date) => {
      const item = filteredData.find((d) => d.date === date);
      if (selectedType === "all" || selectedType === "link") {
        return item ? item.linkAccess : 0;
      }
      return 0;
    });

    datasets.push({
      label: "Tổng số người vào link",
      data: totalLinkAccessData,
      borderColor: "rgba(59, 130, 246, 1)", // Blue
      backgroundColor: "rgba(59, 130, 246, 0.1)",
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: "rgba(59, 130, 246, 1)",
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      yAxisID: "y",
      borderDash: [],
    });

    // Dataset 2: Số người vào link và xem video (ít nhất 1 trong 9 video)
    const videoViewsData = allDates.map((date) => {
      const item = filteredData.find((d) => d.date === date);
      if (selectedType === "all" || selectedType === "button") {
        return item ? item.videoViews : 0;
      }
      return 0;
    });

    datasets.push({
      label: "Vào link và xem video",
      data: videoViewsData,
      borderColor: "rgba(34, 197, 94, 1)", // Green
      backgroundColor: "rgba(34, 197, 94, 0.1)",
      fill: false,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: "rgba(34, 197, 94, 1)",
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      yAxisID: "y",
      borderDash: [],
    });

    // Dataset 3: Số người chỉ vào link không xem video
    const linkOnlyData = allDates.map((date) => {
      const item = filteredData.find((d) => d.date === date);
      if (selectedType === "all" || selectedType === "link") {
        return item ? item.linkOnlyAccess : 0;
      }
      return 0;
    });

    datasets.push({
      label: "Chỉ vào link không xem video",
      data: linkOnlyData,
      borderColor: "rgba(239, 68, 68, 1)", // Red
      backgroundColor: "rgba(239, 68, 68, 0.1)",
      fill: false,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: "rgba(239, 68, 68, 1)",
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      yAxisID: "y",
      borderDash: [5, 5], // Dashed line
    });

    // Dataset 4: Planningpage
    const exploreData = allDates.map((date) => {
      const item = filteredData.find((d) => d.date === date);
      if (selectedType === "all" || selectedType === "explore") {
        return item ? item.exploreAccess : 0;
      }
      return 0;
    });

    datasets.push({
      label: "Planningpage",
      data: exploreData,
      borderColor: "rgba(168, 85, 247, 1)", // Purple
      backgroundColor: "rgba(168, 85, 247, 0.1)",
      fill: false,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: "rgba(168, 85, 247, 1)",
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      yAxisID: "y",
      borderDash: [3, 3], // Dashed line
    });

    return {
      labels,
      datasets,
    };
  }, [filteredData, selectedType]);

  const chartOptions: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: isDarkMode ? "#f3f4f6" : "#374151",
          font: {
            size: 12,
            weight: "600",
          },
          usePointStyle: true,
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.9)" : "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(59, 130, 246, 0.5)",
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            return `${label}: ${value !== null ? value.toLocaleString() : 'N/A'}`;
          },
        },
      },
    },
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        beginAtZero: true,
        title: {
          display: true,
          text: "Số lượng người",
          font: {
            size: 14,
            weight: "600",
          },
          color: isDarkMode ? "#f3f4f6" : "#374151",
        },
        grid: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          stepSize: 1,
          color: isDarkMode ? "#d1d5db" : "#6b7280",
        },
      },
      x: {
        title: {
          display: true,
          text: "Ngày",
          font: {
            size: 14,
            weight: "600",
          },
          color: isDarkMode ? "#f3f4f6" : "#374151",
        },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 15,
          color: isDarkMode ? "#d1d5db" : "#6b7280",
        },
      },
    },
    animation: {
      duration: 2000,
      easing: "easeInOutQuart" as const,
    },
  }), [isDarkMode]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (filteredData.length === 0) return null;

    const totalLinkAccess = filteredData.reduce((sum, item) => sum + item.linkAccess, 0);
    const totalVideoViews = filteredData.reduce((sum, item) => sum + item.videoViews, 0);
    const totalLinkOnlyAccess = filteredData.reduce((sum, item) => sum + item.linkOnlyAccess, 0);
    const totalExploreAccess = filteredData.reduce((sum, item) => sum + item.exploreAccess, 0);
    
    const avgLinkAccess = Math.round(totalLinkAccess / filteredData.length);
    const avgVideoViews = Math.round(totalVideoViews / filteredData.length);
    const avgLinkOnlyAccess = Math.round(totalLinkOnlyAccess / filteredData.length);
    const avgExploreAccess = Math.round(totalExploreAccess / filteredData.length);

    // Tính tỷ lệ phần trăm
    const videoViewRate = totalLinkAccess > 0 
      ? ((totalVideoViews / totalLinkAccess) * 100).toFixed(1)
      : "0";
    const linkOnlyRate = totalLinkAccess > 0
      ? ((totalLinkOnlyAccess / totalLinkAccess) * 100).toFixed(1)
      : "0";
    const exploreRate = totalLinkAccess > 0
      ? ((totalExploreAccess / totalLinkAccess) * 100).toFixed(1)
      : "0";

    return {
      totalLinkAccess,
      totalVideoViews,
      totalLinkOnlyAccess,
      totalExploreAccess,
      avgLinkAccess,
      avgVideoViews,
      avgLinkOnlyAccess,
      avgExploreAccess,
      videoViewRate,
      linkOnlyRate,
      exploreRate,
    };
  }, [filteredData]);

  return (
    <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
      <div className="mb-4">
        {/* Header with title and view toggle */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Phân tích Truy cập Link và Xem Video
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode("chart")}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === "chart"
                    ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                }`}
                title="Hiển thị dạng biểu đồ"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === "table"
                    ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                }`}
                title="Hiển thị dạng bảng"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {viewMode === "chart"
            ? "Biểu đồ hiển thị tổng số người vào link, số người vào link và xem video (ít nhất 1 trong 9 video), và số người chỉ vào link không xem video."
            : "Bảng hiển thị chi tiết số lượng người vào link và xem video theo từng ngày."}
        </p>

        {error && (
          <div className="mb-3 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Loại:
            </label>
            <select
              value={selectedType}
              onChange={(e) =>
                setSelectedType(e.target.value as "link" | "button" | "explore" | "all")
              }
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tất cả</option>
              <option value="link">Vào link</option>
              <option value="button">Xem video</option>
              <option value="explore">Planningpage</option>
            </select>
          </div>

          {/* Month Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Chọn tháng:
            </label>
            <select
              value={selectedMonth === null ? "" : selectedMonth}
              onChange={(e) =>
                setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)
              }
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tất cả tháng</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((month) => (
                <option key={month} value={month}>
                  Tháng {month}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Từ ngày:
            </label>
            <DateInputDDMMYYYY
              valueISO={startDate}
              onChangeISO={setStartDate}
              fallbackISO={defaultStartDate}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Đến ngày:
            </label>
            <DateInputDDMMYYYY
              valueISO={endDate}
              onChangeISO={setEndDate}
              fallbackISO={defaultEndDate}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Clear Filters Button */}
          {(selectedType !== "all" ||
            selectedMonth !== null ||
            startDate !== defaultStartDate ||
            endDate !== defaultEndDate) && (
            <button
              onClick={() => {
                setSelectedType("all");
                setSelectedMonth(null);
                setStartDate(defaultStartDate);
                setEndDate(defaultEndDate);
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span>Xóa lọc</span>
            </button>
          )}
        </div>

        {/* Summary Stats */}
        {summaryStats && (
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Link Access Stats */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    Tổng số người vào link
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Tổng: </span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {(selectedType === "all" || selectedType === "link" 
                      ? summaryStats.totalLinkAccess 
                      : 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Trung bình/ngày: </span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {(selectedType === "all" || selectedType === "link" 
                      ? summaryStats.avgLinkAccess 
                      : 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Video Views Stats */}
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    Vào link và xem video
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Tổng: </span>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    {(selectedType === "all" || selectedType === "button" 
                      ? summaryStats.totalVideoViews 
                      : 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Trung bình/ngày: </span>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    {(selectedType === "all" || selectedType === "button" 
                      ? summaryStats.avgVideoViews 
                      : 0).toLocaleString()}
                  </span>
                </div>
                {selectedType === "all" && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Tỷ lệ: </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {summaryStats.videoViewRate}%
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Link Only Access Stats */}
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    Chỉ vào link không xem video
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Tổng: </span>
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {(selectedType === "all" || selectedType === "link" 
                      ? summaryStats.totalLinkOnlyAccess 
                      : 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Trung bình/ngày: </span>
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {(selectedType === "all" || selectedType === "link" 
                      ? summaryStats.avgLinkOnlyAccess 
                      : 0).toLocaleString()}
                  </span>
                </div>
                {selectedType === "all" && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Tỷ lệ: </span>
                    <span className="font-semibold text-red-600 dark:text-red-400">
                      {summaryStats.linkOnlyRate}%
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Explore Access Stats */}
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                    Planningpage
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Tổng: </span>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                    {(selectedType === "all" || selectedType === "explore" 
                      ? summaryStats.totalExploreAccess 
                      : 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Trung bình/ngày: </span>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                    {(selectedType === "all" || selectedType === "explore" 
                      ? summaryStats.avgExploreAccess 
                      : 0).toLocaleString()}
                  </span>
                </div>
                {selectedType === "all" && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Tỷ lệ: </span>
                    <span className="font-semibold text-purple-600 dark:text-purple-400">
                      {summaryStats.exploreRate}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {viewMode === "chart" ? (
        <div className="relative h-80">
          {filteredData.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              Không có dữ liệu để hiển thị
            </div>
          )}
        </div>
      ) : (
        <div className="relative overflow-x-auto min-h-[20rem]">
          {filteredData.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ngày
                  </th>
                  {selectedType === "all" && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tổng số người vào link
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Vào link và xem video
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Chỉ vào link không xem video
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Planningpage
                      </th>
                    </>
                  )}
                  {selectedType === "link" && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tổng số người vào link
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Chỉ vào link không xem video
                      </th>
                    </>
                  )}
                  {selectedType === "button" && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Vào link và xem video
                    </th>
                  )}
                  {selectedType === "explore" && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Planningpage
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredData.map((item) => (
                  <tr key={item.date} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(item.date).toLocaleDateString("vi-VN", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                    {selectedType === "all" && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400 font-semibold">
                          {item.linkAccess.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-semibold">
                          {item.videoViews.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400 font-semibold">
                          {item.linkOnlyAccess.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 dark:text-purple-400 font-semibold">
                          {item.exploreAccess.toLocaleString()}
                        </td>
                      </>
                    )}
                    {selectedType === "link" && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400 font-semibold">
                          {item.linkAccess.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400 font-semibold">
                          {item.linkOnlyAccess.toLocaleString()}
                        </td>
                      </>
                    )}
                    {selectedType === "button" && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-semibold">
                        {item.videoViews.toLocaleString()}
                      </td>
                    )}
                    {selectedType === "explore" && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 dark:text-purple-400 font-semibold">
                        {item.exploreAccess.toLocaleString()}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-80 text-gray-500 dark:text-gray-400">
              Không có dữ liệu để hiển thị
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CombinedAccessViewsChart;
